import { IBuyBonusRequest, IGambleRequest, ISpinRequest } from '@/modules'
import GameSignals from '@/signals/GameSignals'
import { ServerRequests } from './dg/ServerRequests'
import { Model } from '@/model/Model'
import { ModulesManager } from '@modules/src/managers/ModulesManager'
import { SessionTelemetryModule } from '@modules/src/modules/SessionTelemetryModule'

export class BackendManager {
    private readonly _requests: ServerRequests
    private _model: Model

    public constructor(model: Model) {
        this._requests = new ServerRequests(model)
        this._model = model

        GameSignals.getInitResponse.connect(this.onGetInitResponse)
        GameSignals.getSpinResponse.connect(this.onGetSpinResponse)
        GameSignals.getBalanceResponse.connect(this.onGetBalanceResponse)
        GameSignals.backendMessage.connect(this.onBackendMessage)
        GameSignals.getBuyBonusResponse.connect(this.onGetBuyBonusResponse)
        ;(GameSignals as any).getGambleResponse.connect(this.onGetGambleResponse)
        ;(GameSignals as any).promoFreeSpinsDeclined.connect(this.onPromoFreeSpinsDeclined)
    }

    private onGetInitResponse = () => {
        this._requests.getInit()
    }

    private onGetSpinResponse = (data: ISpinRequest) => {
        this._requests.getSpin(data)
    }

    private onGetBalanceResponse = () => {
        try {
            this._requests.getBalance()
        } catch (error) {
            console.warn('[BackendManager] getBalance request failed, using local fallback balance', error)
            GameSignals.balanceUpdate.emit(this._model.balance)
        }
    }

    private onGetBuyBonusResponse = (data: IBuyBonusRequest) => {
        this._requests.getBuyBonus(data)
    }

    private onGetGambleResponse = (data: IGambleRequest) => {
        this._requests.getGamble(data)
    }

    private onPromoFreeSpinsDeclined = (data: any) => {
        const bfsRecordId =
            data?.promo_details?.bfs_record_id ??
            data?.next_round?.bfs_record_id ??
            data?.bfs_record_id ??
            this._model.spinData?.promo_details?.bfs_record_id ??
            (this._model.initData as any)?.promo_details?.bfs_record_id
        this.cancelPromoFreeSpins(bfsRecordId)
    }

    private onBackendMessage = (message: any) => {
        switch (message.action) {
            case 'init':
                GameSignals.initResponse.emit(message.data)
                break

            case 'spin':
                GameSignals.spinResponse.emit(message.data)
                // Emit economy telemetry for round settlement
                this.emitSpinEconomyEvents(message.data)
                break

            case 'balance': {
                if (message.data && message.data.balance != null) {
                    GameSignals.balanceUpdate.emit(message.data.balance)
                    this._model.balance = message.data.balance
                }

                break
            }

            case 'terminate':
                this._requests.getTerminate()
                break

            case 'buyBonus':
                GameSignals.buyBonusResponse.emit(message.data)
                // Emit economy telemetry for bonus purchase
                this.emitBonusBuyEvent(message.data)
                break

            case 'gamble': {
                if (message.data?.player?.balance != null) {
                    GameSignals.balanceUpdate.emit(message.data.player.balance)
                    this._model.balance = Number(message.data.player.balance)
                }

                ;(GameSignals as any).gambleResponse.emit(message.data)
                break
            }

            case 'claimPFS': {
                const hasError = Boolean(
                    message.data?.error || message.data?.error_number || message.data?.error_code
                )
                const gs: any = GameSignals
                if (hasError) {
                    gs.promoFreeSpinsClaimFailed?.emit(message.data)
                    break
                }
                gs.promoFreeSpinsClaimed?.emit(message.data)
                break
            }

            case 'cancelPFS': {
                try {
                    console.log('[BackendManager] Cancel PFS response received:', message.data)
                    
                    // Un cancel rifiutato lascia la promo pendente sul server: dichiararla
                    // declinata nasconderebbe un'offerta che il giocatore non può più gestire.
                    if (message.data?.error || message.data?.error_number || message.data?.error_code) {
                        console.error('[BackendManager] Cancel PFS error:', {
                            error: message.data.error,
                            error_number: message.data.error_number,
                            error_code: message.data.error_code,
                            error_message: message.data.error_message,
                            full_response: message.data
                        })
                        GameSignals.casinoError.emit(message.data)
                        break
                    }
                    
                    // Aggiorna il balance se presente nella risposta. Se manca non si richiede
                    // nulla: la campagna non è mai stata accettata, quindi il saldo non è cambiato.
                    if (message.data && message.data.balance != null) {
                        GameSignals.balanceUpdate.emit(message.data.balance)
                        this._model.balance = message.data.balance
                    }
                    
                    // Emetti segnale per notificare che i promo sono stati cancellati
                    const gs: any = GameSignals
                    gs.promoDeclined?.emit(message.data)
                } catch (error) {
                    console.error('[BackendManager] Error processing cancel PFS response:', error, message.data)
                }
                
                break
            }

            case 'tournament': {
                try {
                    console.log('[BackendManager] Tournament response received:', message.data)

                    // The model is written by TournamentManager, which merges onto what init
                    // delivered. Replacing it here dropped every key the payload does not repeat:
                    // the leaderboard endpoint answers with the standings alone, so the prize
                    // table emptied and the joined flag — hence the trophy — went with it.

                    // Emetti segnale per aggiornare la UI
                    const gs: any = GameSignals
                    gs.tournamentDataReceived?.emit(message.data)
                } catch (error) {
                    console.error('[BackendManager] Error processing tournament response:', error, message.data)
                }
                
                break
            }
        }
    }

    public cancelPromoFreeSpins(bfsRecordId?: number | string) {
        this._requests.cancelPromoFreeSpins(bfsRecordId)
    }

    public claimPromoFreeSpins(bfsRecordId: number | string) {
        this._requests.claimPromoFreeSpins(bfsRecordId)
    }

    public getTournamentLeaderboard(tournamentId: number) {
        this._requests.getTournamentLeaderboard(tournamentId)
    }

    public refreshTournamentLeaderboardInBackground(tournamentId: number) {
        void this._requests.refreshTournamentLeaderboardInBackground(tournamentId)
    }

    public joinTournament(tournamentId: number) {
        this._requests.joinTournament(tournamentId)
    }

    /**
     * Get the telemetry module for emitting economy events
     */
    private getTelemetryModule(): SessionTelemetryModule | null {
        try {
            return ModulesManager.getModule('sessionTelemetry') as SessionTelemetryModule | null
        } catch {
            return null
        }
    }

    /**
     * Emit economy telemetry event
     */
    private emitEconomyEvent(eventType: string, payload: Record<string, any>): void {
        const telemetryModule = this.getTelemetryModule()
        if (!telemetryModule) return

        try {
            (telemetryModule as any).emitEconomyEvent(eventType, payload)
        } catch (error) {
            console.warn('[BackendManager] Failed to emit economy event:', error)
        }
    }

    /**
     * Emit round_settled and wager_accepted events when spin response is received
     */
    private emitSpinEconomyEvents(spinData: any): void {
        if (!spinData) return

        const roundId = spinData.round_id || spinData.id || `round-${Date.now()}`
        const betAmount = Number(spinData.bet_amount || spinData.wager || 0) * 100 // convert to minor units
        const payoutAmount = Number(spinData.win_currency || spinData.payout_amount || 0) * 100
        const valueType = spinData.value_type || 'real_money'
        const currency = spinData.currency || 'USD'
        const isFreeSpinRound = !!spinData.freeround || !!spinData.freebet
        const betKind = isFreeSpinRound ? 'free_spin' : 'base'

        // Emit round_settled event
        this.emitEconomyEvent('round_settled', {
            round_id: roundId,
            value_type: valueType,
            bet_amount_minor: betAmount,
            payout_amount_minor: payoutAmount,
            currency: currency,
            bet_kind: betKind,
            settle_duration_ms: spinData.settle_duration_ms || 0,
        })
    }

    /**
     * Emit wager_accepted event when spin request is made
     */
    public emitWagerAcceptedEvent(bet: number, roundId: string): void {
        const betAmount = Math.round(bet * 100) // convert to minor units
        this.emitEconomyEvent('wager_accepted', {
            round_id: roundId,
            value_type: 'real_money',
            bet_amount_minor: betAmount,
            currency: 'USD',
            bet_kind: 'base',
        })
    }

    /**
     * Emit bonus_buy_purchased event
     */
    private emitBonusBuyEvent(buyBonusData: any): void {
        if (!buyBonusData) return

        const roundId = buyBonusData.round_id || buyBonusData.id || `bonus-${Date.now()}`
        const costAmount = Number(buyBonusData.cost || buyBonusData.cost_minor || 0)

        this.emitEconomyEvent('bonus_buy_purchased', {
            round_id: roundId,
            value_type: 'real_money',
            cost_minor: costAmount,
            currency: 'USD',
            feature_kind: buyBonusData.feature_kind || 'bonus_buy',
        })
    }
}

